/**
 * React Query hook for centralized pricing
 *
 * Fetches pricing from the backend's single source of truth.
 * All pricing display in the frontend should use this hook.
 */

import { useQuery } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

// Query key factory
export const pricingKeys = {
  all: ['pricing'],
  table: () => [...pricingKeys.all, 'table'],
  feature: (featureId) => [...pricingKeys.all, 'feature', featureId],
};

/**
 * Fetch the full pricing table from the API.
 */
async function fetchPricingTable() {
  const response = await fetch(`${API_BASE}/pricing`);
  if (!response.ok) {
    throw new Error('Failed to fetch pricing');
  }
  return response.json();
}

/**
 * Hook to fetch the full pricing table.
 *
 * Returns:
 *   - features: all features with pricing
 *   - free_features: features that are free
 *   - paid_features: features that have a cost
 *   - volume_discounts: volume discount tiers
 *   - subscription_plans: available subscription plans
 */
export function usePricing(options = {}) {
  return useQuery({
    queryKey: pricingKeys.table(),
    queryFn: fetchPricingTable,
    staleTime: 1000 * 60 * 60, // 1 hour - pricing rarely changes
    retry: 2,
    // Fallback to prevent UI breakage if API fails
    placeholderData: {
      features: [],
      free_features: [],
      paid_features: [],
      volume_discounts: [],
      subscription_plans: [],
    },
    ...options,
  });
}

/**
 * Hook to get pricing for a specific feature.
 *
 * @param {string} featureId - The feature ID (e.g., 'active_liveness', 'aml_screening')
 * @returns {object|null} Feature pricing or null if not found
 */
export function useFeaturePrice(featureId) {
  const { data, isLoading, error } = usePricing();

  if (isLoading || error || !data?.features) {
    return { price: null, isLoading, error };
  }

  const feature = data.features.find((f) => f.id === featureId);
  return {
    price: feature || null,
    isLoading: false,
    error: null,
  };
}

/**
 * Helper to format a price for display.
 *
 * @param {object} feature - Feature object from usePricing
 * @returns {string} Formatted price (e.g., 'Free', '$0.15', 'Variable')
 */
export function formatPrice(feature) {
  if (!feature) return '...';
  return feature.price_display || (feature.is_free ? 'Free' : `$${feature.price_dollars?.toFixed(2)}`);
}

/**
 * Helper to get price per unit text (e.g., '$0.15/check').
 *
 * @param {object} feature - Feature object from usePricing
 * @param {string} unit - Unit label (e.g., 'check', 'doc', 'screen')
 * @returns {string} Formatted price with unit
 */
export function formatPricePerUnit(feature, unit = 'call') {
  if (!feature) return '...';
  if (feature.is_free) return 'Free';
  if (feature.price_dollars === null) return 'Variable';
  return `$${feature.price_dollars.toFixed(2)}/${unit}`;
}

export default usePricing;
